import { secureStorage } from './secureStorage';
import { safeConsole } from './piiProtection';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.clinvea.com';

export interface PdfOptions {
  prescriptionId: string;
  action: 'download' | 'print';
  patientName?: string;
  prescriptionDate?: string;
}

/**
 * Fetches a prescription PDF from the backend and either downloads it or opens it for printing
 * @param options - Configuration for PDF handling
 * @param userRole - Optional user role to determine endpoint
 */
export async function handlePrescriptionPdf(options: PdfOptions): Promise<{ success: boolean; error?: string }> {
  const { prescriptionId, action, patientName, prescriptionDate } = options;

  try {
    const token = secureStorage.getAuthToken();
    const headers: Record<string, string> = {
      'X-Client-Version': '1.0.0',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Determine endpoint based on user role
    // Default to doctor endpoint for backward compatibility
    let endpoint = `${API_BASE_URL}/api/doctor/prescriptions/${prescriptionId}/download-pdf`;
    
    // For pharmacy users, they might use a different endpoint if one exists
    // For now, we keep the same endpoint as the authorization is handled by the backend

    const response = await fetch(endpoint, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        secureStorage.removeAuthToken();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('You do not have permission to download this prescription');
      }
      throw new Error('Failed to load PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Generate filename with patient name and date
    let fileName = `prescription-${prescriptionId}.pdf`;

    // If patient name and date are provided, use them for a better filename
    if (patientName) {
      // Sanitize patient name for filename (remove special characters)
      const sanitizedName = patientName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 30);

      if (prescriptionDate) {
        try {
          // Format: Prescription_PatientName_DDMMMYYYY.pdf
          const dateObj = new Date(prescriptionDate);

          // Validate the date is valid
          if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[dateObj.getMonth()];
            const year = dateObj.getFullYear();
            fileName = `Prescription_${sanitizedName}_${day}${month}${year}.pdf`;
          } else {
            // Date is invalid, use just patient name
            fileName = `Prescription_${sanitizedName}.pdf`;
          }
        } catch (e) {
          // If date parsing fails, use just patient name
          fileName = `Prescription_${sanitizedName}.pdf`;
        }
      } else {
        // No date provided, use just patient name
        fileName = `Prescription_${sanitizedName}.pdf`;
      }
    } else {
      // Try to extract from Content-Disposition header as last resort
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    if (action === 'download') {
      // Download the PDF
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up immediately for downloads
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } else {
      // Open for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        throw new Error('Popup blocked. Please allow popups to print.');
      }
      
      // Clean up after a delay for print
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to process PDF';
    safeConsole.error('PDF error:', error);
    return { success: false, error: errorMessage };
  }
}
